import { useMemo, useEffect, useRef } from "react";
import { Autocomplete, Box, CircularProgress, Dialog, Stack, TextField, Typography, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Button as FoodsButton, Typography as FoodsTypography, ToggleSwitch } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { type Control, Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSnackbar } from "src/utils/useSnackbar";
import { useCreateUser } from "src/api/client-api/user/useCreateUser";
import { User } from "src/api/client-api/user/useAllUsers";
import { JsonAttributesField } from "src/components/JsonAttributes/JsonAttributesField";
import { ASSIGN, ROLE, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useRoles } from "src/api/client-api/roles/useRoles";
import { OrganizationRolesSection } from "src/pages/Settings/Users/OrganizationRolesSection";
import { buildUserModalPayload } from "src/pages/Settings/Users/userModalPayload";
import {
    canManagePlatformAdminTarget,
    derivePlatformRoleOptions,
    deriveOrganizationRoleOptions,
    isPlatformAdmin,
    selectPlatformRoleCodes,
    shouldSubmitRolesOnEdit,
} from "src/pages/Settings/Users/userModalRoles";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

// Zod schema for User form validation
const UserFormSchema = z.object({
    id: z.number().optional(),
    firstname: z.string().min(1, "First name is required"),
    lastname: z.string().min(1, "Last name is required"),
    email: z.string().email({ message: "Invalid email address" }),
    phone: z.string().optional(),
    companies: z.array(z.any()).optional(),
    roles: z.array(z.string()).optional(),
    platformAdmin: z.boolean().optional(),
    jsonAttributes: z.record(z.string(), z.any()).optional(),
});

type UserFormData = z.infer<typeof UserFormSchema>;

type UserModalProps = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    onCreatedForEdit?: (createdUser: User) => void;
    editingUser?: User | null;
};

export const UserModal = (props: UserModalProps) => {
    const { open, onClose, onSuccess, onCreatedForEdit, editingUser } = props;
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const { canUser, entitiesJsonAttributes, isAdmin } = useFoodpilotContext();
    const { roles, rolesLoading, getRoleDisplayName } = useRoles();
    const actorIsPlatformAdmin = isAdmin();
    const canAssignRoles = canUser(ASSIGN, ROLE);
    const canManageOrganizationRoles =
        canAssignRoles && canManagePlatformAdminTarget(actorIsPlatformAdmin, editingUser?.roles);

    const userJsonAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.user] || [];
    const isEditing = !!editingUser;

    const createUserMutation = useCreateUser();
    const isProcessing = createUserMutation.isLoading;
    const submitIntent = useRef<"save" | "configure">("save");

    const organizationRoleOptions = useMemo(
        () => deriveOrganizationRoleOptions(roles, getRoleDisplayName),
        [roles, getRoleDisplayName],
    );

    const platformRoleOptions = useMemo(
        () => derivePlatformRoleOptions(roles, getRoleDisplayName),
        [roles, getRoleDisplayName],
    );

    const {
        control,
        handleSubmit,
        formState: { errors, isValid, dirtyFields },
        reset,
        setValue,
        watch,
    } = useForm<UserFormData>({
        mode: "onChange",
        resolver: zodResolver(UserFormSchema),
        defaultValues: {
            id: undefined,
            firstname: "",
            lastname: "",
            email: "",
            phone: "",
            companies: [],
            roles: [],
            platformAdmin: false,
            jsonAttributes: {},
        },
    });

    const selectedCompanies = watch("companies") || [];
    const hasProfileChanges = Boolean(
        dirtyFields.firstname ||
        dirtyFields.lastname ||
        dirtyFields.email ||
        dirtyFields.phone ||
        dirtyFields.platformAdmin ||
        dirtyFields.roles ||
        dirtyFields.jsonAttributes,
    );
    const hasRoleChanges = Boolean(dirtyFields.platformAdmin || dirtyFields.roles);
    const isPlatformRoleCatalogReady = !rolesLoading && roles !== undefined;
    const isSubmitDisabled =
        !isValid ||
        isProcessing ||
        (isEditing && !hasProfileChanges) ||
        (actorIsPlatformAdmin && hasRoleChanges && !isPlatformRoleCatalogReady);
    const isRolesSelectDisabled = rolesLoading;

    const updateButton = (
        <FoodsButton variant="primary" type="submit" form="user-modal-form" disabled={isSubmitDisabled}>
            {isProcessing ?
                <CircularProgress size={20} color="inherit" />
            :   t("users.update", "Update")}
        </FoodsButton>
    );

    const createFooter = (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <FoodsButton
                variant="secondary"
                type="submit"
                form="user-modal-form"
                disabled={isSubmitDisabled}
                onClick={() => {
                    submitIntent.current = "save";
                }}
            >
                {isProcessing ?
                    <CircularProgress size={20} color="inherit" />
                :   t("users.save", "Save")}
            </FoodsButton>
            {canAssignRoles && (
                <FoodsButton
                    variant="primary"
                    type="submit"
                    form="user-modal-form"
                    disabled={isSubmitDisabled}
                    onClick={() => {
                        submitIntent.current = "configure";
                    }}
                >
                    {isProcessing ?
                        <CircularProgress size={20} color="inherit" />
                    :   t("users.createAndConfigureRoles", "Create and configure roles")}
                </FoodsButton>
            )}
        </Stack>
    );

    // Reset form when modal opens or editingUser changes
    useEffect(() => {
        if (open) {
            if (editingUser) {
                // Pre-fill form for editing
                reset({
                    id: editingUser.id,
                    firstname: editingUser.firstname || "",
                    lastname: editingUser.lastname || "",
                    email: editingUser.email || "",
                    phone: editingUser.phone || "",
                    companies: editingUser.organizations || [],
                    roles: editingUser.roles.filter((role) => role !== "ROLE_USER" && role !== "ROLE_ADMIN") || [],
                    platformAdmin: isPlatformAdmin(editingUser.roles),
                    jsonAttributes: (editingUser as any).jsonAttributes || {},
                });
            } else {
                // Reset form for creating
                reset({
                    id: undefined,
                    firstname: "",
                    lastname: "",
                    email: "",
                    phone: "",
                    companies: [],
                    roles: [],
                    platformAdmin: false,
                    jsonAttributes: {},
                });
            }
        }
    }, [open, editingUser, reset]);

    const onSubmitHandler: SubmitHandler<UserFormData> = async (formData) => {
        try {
            const userData = buildUserModalPayload(formData, {
                isEditing,
                hasUserJsonAttributes: userJsonAttributes.length > 0,
                includeRolesOnEdit: shouldSubmitRolesOnEdit(actorIsPlatformAdmin, dirtyFields),
                platformRoleOptions,
            });

            const response = await createUserMutation.mutateAsync({
                user: userData,
            });

            setSnackbarProps({
                message:
                    isEditing ?
                        t("users.updateSuccess", "User updated successfully")
                    :   t("users.createSuccess", "User created successfully"),
                type: "success",
            });

            const shouldConfigureRoles = !isEditing && submitIntent.current === "configure";
            if (shouldConfigureRoles) {
                onCreatedForEdit?.(response.data as User);
                return;
            }

            onSuccess();
            onClose();
        } catch (error) {
            setSnackbarProps({
                message:
                    isEditing ?
                        t("users.updateError", "Failed to update user")
                    :   t("users.createError", "Failed to create user"),
                type: "error",
            });
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "calc(100% - 64px)",
                    marginTop: "0px",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: (theme) => `1px solid ${theme.custom.grey[500]}`,
                    backgroundColor: "white",
                    flexShrink: 0,
                }}
            >
                <Box>
                    <Typography
                        variant="h3"
                        sx={{ lineHeight: "1.6em", color: (theme) => theme.palette.text.primary, marginBottom: "8px" }}
                    >
                        {isEditing ? t("users.editTitle", "Edit User") : t("users.createTitle", "Create User")}
                    </Typography>
                    <Typography
                        variant="small-medium"
                        sx={{ lineHeight: "1.6em", color: (theme) => theme.custom.grey[2500] }}
                    >
                        {isEditing ?
                            t("users.editDescription", "Update the user information.")
                        :   t("users.createDescription", "Set up a new user account.")}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        cursor: "pointer",
                        padding: "8px",
                        borderRadius: "50%",
                        "&:hover": {
                            backgroundColor: (theme) => theme.custom.grey[300],
                        },
                    }}
                    onClick={handleClose}
                >
                    <CloseIcon />
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ padding: "32px", flex: 1, overflowY: "auto" }}>
                <form id="user-modal-form" onSubmit={handleSubmit(onSubmitHandler)}>
                    <Stack spacing={3}>
                        {/* Name Fields */}
                        <Stack direction="row" spacing={2}>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        lineHeight: "1.6em",
                                        color: (theme) => theme.custom.grey[2800],
                                        marginBottom: "8px",
                                    }}
                                >
                                    {t("users.firstnameLabel", "First Name")}*:
                                </Typography>
                                <Controller
                                    name="firstname"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            error={!!errors.firstname}
                                            helperText={errors.firstname?.message}
                                            placeholder={t("users.firstnamePlaceholder", "Enter first name")}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "24px",
                                                    minHeight: "40px",
                                                    backgroundColor: "white",
                                                    "& fieldset": {
                                                        borderColor: (theme) => theme.custom.grey[500],
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: (theme) => theme.custom.grey[800],
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: (theme) => theme.palette.primary[600],
                                                    },
                                                },
                                                "& .MuiInputBase-input": {
                                                    fontSize: "0.875rem",
                                                    padding: "0px 16px !important",
                                                    color: (theme) => theme.custom.grey[2800],
                                                    "&::placeholder": {
                                                        color: (theme) => theme.custom.grey[1400],
                                                        opacity: 1,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        lineHeight: "1.6em",
                                        color: (theme) => theme.custom.grey[2800],
                                        marginBottom: "8px",
                                    }}
                                >
                                    {t("users.lastnameLabel", "Last Name")}*:
                                </Typography>
                                <Controller
                                    name="lastname"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            error={!!errors.lastname}
                                            helperText={errors.lastname?.message}
                                            placeholder={t("users.lastnamePlaceholder", "Enter last name")}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "24px",
                                                    minHeight: "40px",
                                                    backgroundColor: "white",
                                                    "& fieldset": {
                                                        borderColor: (theme) => theme.custom.grey[500],
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: (theme) => theme.custom.grey[800],
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: (theme) => theme.palette.primary[600],
                                                    },
                                                },
                                                "& .MuiInputBase-input": {
                                                    fontSize: "0.875rem",
                                                    padding: "0px 16px !important",
                                                    color: (theme) => theme.custom.grey[2800],
                                                    "&::placeholder": {
                                                        color: (theme) => theme.custom.grey[1400],
                                                        opacity: 1,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        </Stack>

                        {/* Email Field */}
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    lineHeight: "1.6em",
                                    color: (theme) => theme.custom.grey[2800],
                                    marginBottom: "8px",
                                }}
                            >
                                {t("users.emailLabel", "Email")}*:
                            </Typography>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        type="email"
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                        placeholder={t("users.emailPlaceholder", "Enter email address")}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "24px",
                                                minHeight: "40px",
                                                backgroundColor: "white",
                                                "& fieldset": {
                                                    borderColor: (theme) => theme.custom.grey[500],
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: (theme) => theme.custom.grey[800],
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "primary.main",
                                                },
                                            },
                                            "& .MuiInputBase-input": {
                                                fontSize: "0.875rem",
                                                padding: "0px 16px !important",
                                                color: (theme) => theme.custom.grey[2800],
                                                "&::placeholder": {
                                                    color: (theme) => theme.custom.grey[1400],
                                                    opacity: 1,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        {/* Phone Field */}
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    lineHeight: "1.6em",
                                    color: (theme) => theme.custom.grey[2800],
                                    marginBottom: "8px",
                                }}
                            >
                                {t("users.phoneLabel", "Phone")}:
                            </Typography>
                            <Controller
                                name="phone"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        placeholder={t("users.phonePlaceholder", "Enter phone number")}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "24px",
                                                minHeight: "40px",
                                                backgroundColor: "white",
                                                "& fieldset": {
                                                    borderColor: (theme) => theme.custom.grey[500],
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: (theme) => theme.custom.grey[800],
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "primary.main",
                                                },
                                            },
                                            "& .MuiInputBase-input": {
                                                fontSize: "0.875rem",
                                                padding: "0px 16px !important",
                                                color: (theme) => theme.custom.grey[2800],
                                                "&::placeholder": {
                                                    color: (theme) => theme.custom.grey[1400],
                                                    opacity: 1,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        {actorIsPlatformAdmin && (
                            <Box>
                                <FoodsTypography
                                    variant="h6"
                                    sx={(theme) => ({ color: theme.custom.grey[2800], marginBottom: theme.spacing(1) })}
                                >
                                    {t("users.platformLevelRole", "Platform-level role")}:
                                </FoodsTypography>
                                <Controller
                                    name="platformAdmin"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <ToggleSwitch
                                            key={`platform-admin-${value ? "on" : "off"}`}
                                            defaultChecked={!!value}
                                            onChange={(event) => onChange(event.target.checked)}
                                            label={t("users.platformAdmin", "Platform Admin")}
                                            toggleSize="medium"
                                        />
                                    )}
                                />
                            </Box>
                        )}

                        {actorIsPlatformAdmin && (
                            <Box>
                                <FoodsTypography
                                    variant="h6"
                                    sx={(theme) => ({ color: theme.custom.grey[2800], marginBottom: theme.spacing(1) })}
                                >
                                    {t("users.platformRolesLabel", "Platform roles")}:
                                </FoodsTypography>
                                <FoodsTypography
                                    variant="small-medium"
                                    sx={(theme) => ({
                                        display: "block",
                                        color: theme.custom.grey[2000],
                                        marginBottom: theme.spacing(1),
                                    })}
                                >
                                    {t(
                                        "users.platformRolesHelper",
                                        "Granted platform-wide, independent of any organization.",
                                    )}
                                </FoodsTypography>
                                <Controller
                                    name="roles"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Autocomplete
                                            multiple
                                            value={selectPlatformRoleCodes(value, platformRoleOptions)}
                                            onChange={(_, newValue) => onChange(newValue)}
                                            options={platformRoleOptions}
                                            getOptionLabel={getRoleDisplayName}
                                            loading={rolesLoading}
                                            disabled={isRolesSelectDisabled}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder={t(
                                                        "users.platformRolesPlaceholder",
                                                        "Select platform roles",
                                                    )}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "24px",
                                                            minHeight: "40px",
                                                            backgroundColor: "white",
                                                            "& fieldset": {
                                                                borderColor: (theme) => theme.custom.grey[500],
                                                            },
                                                            "&:hover fieldset": {
                                                                borderColor: (theme) => theme.custom.grey[800],
                                                            },
                                                            "&.Mui-focused fieldset": {
                                                                borderColor: "primary.main",
                                                            },
                                                        },
                                                        "& .MuiInputBase-input": {
                                                            fontSize: "0.875rem",
                                                            padding: "0px 16px !important",
                                                            color: (theme) => theme.custom.grey[2800],
                                                            "&::placeholder": {
                                                                color: (theme) => theme.custom.grey[1400],
                                                                opacity: 1,
                                                            },
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Box>
                        )}

                        {/* JSON Attributes Field */}
                        {userJsonAttributes.length > 0 && (
                            <>
                                <Divider sx={{ borderColor: (theme) => theme.custom.grey[500] }} />
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            lineHeight: "1.6em",
                                            color: (theme) => theme.custom.grey[2800],
                                            marginBottom: "16px",
                                        }}
                                    >
                                        {t("users.additionalAttributes", "Additional Attributes")}
                                    </Typography>
                                    <JsonAttributesField
                                        control={control as Control<any>}
                                        jsonAttributes={userJsonAttributes}
                                    />
                                </Box>
                            </>
                        )}

                        {isEditing && (
                            <>
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>{updateButton}</Box>
                                {canAssignRoles && (
                                    <>
                                        <Divider sx={{ borderColor: (theme) => theme.custom.grey[500] }} />
                                        <OrganizationRolesSection
                                            userId={editingUser.id}
                                            companies={selectedCompanies}
                                            roleOptions={organizationRoleOptions}
                                            isRolesLoading={rolesLoading}
                                            canManageRoles={canManageOrganizationRoles}
                                            isTargetPlatformAdmin={isPlatformAdmin(editingUser.roles)}
                                            onCompaniesChange={(nextCompanies) =>
                                                setValue("companies", nextCompanies, {
                                                    shouldDirty: false,
                                                    shouldValidate: true,
                                                })
                                            }
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </Stack>
                </form>
            </Box>

            {!isEditing && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        padding: "0px 24px 0px 32px",
                        height: "72px",
                        backgroundColor: "white",
                        borderTop: (theme) => `1px solid ${theme.custom.grey[500]}`,
                        flexShrink: 0,
                    }}
                >
                    {createFooter}
                </Box>
            )}
        </Dialog>
    );
};
